import { useState } from 'react';
import { useSelector, useDispatch } from '../actions/types';
import { Actions as ArticleFeedbackActions } from '../actions/articleFeedback';
import {
  getFeedbackEmailFieldHidden,
  getFeedbackEnabled,
  getUser,
} from '../utils/globals';
import { getOnChangeValue } from '../utils';
import useTranslation from '../hooks/useTranslation';
import '../i18n';

import { Stage } from '../reducers/articleFeedback';
import useArticleFragment from './useArticleFragment';

export type FeedbackFormShape = {
  email?: string;
  feedback: string;
};

type UseFeedbackReturn = {
  stage: Stage;
  isFeedbackEnabled: boolean;
  isLoading: boolean;
  isFeedbackFormSubmitting: boolean;
  values: FeedbackFormShape;
  emailFieldIsHidden: boolean;
  showAnonymousWarning: boolean;
  hasSubmitted: boolean;
  messageError: string | null;
  emailError: string | null;
  onPositiveReaction: () => void;
  onNegativeReaction: () => void;
  onFeedbackSkipped: () => void;
  handleMessageChange: (
    event: React.ChangeEvent<HTMLTextAreaElement> | string
  ) => void;
  handleEmailChange: (
    event: React.ChangeEvent<HTMLInputElement> | string
  ) => void;
  onSubmit: (event?: React.FormEvent<HTMLFormElement>) => void;
};

export default function useFeedback(_articleId?: string): UseFeedbackReturn {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const user = getUser();
  const emailFieldIsHidden = getFeedbackEmailFieldHidden() || !!user.email;
  const initialValues = {
    email: emailFieldIsHidden ? undefined : '',
    feedback: '',
  };
  const [values, setValues] = useState(initialValues);

  const article = useArticleFragment(_articleId);
  const [showAnonymousWarning, setShowAnonymousWarning] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  if (!article) throw new Error('Unable to find article for ArticleFeedback');
  const articleId = article.id;

  const handlePositive = () =>
    dispatch(
      ArticleFeedbackActions.feedbackReactionPressed(
        articleId,
        article.title,
        true
      )
    );

  const handleNegative = () =>
    dispatch(
      ArticleFeedbackActions.feedbackReactionPressed(
        articleId,
        article.title,
        false
      )
    );

  const handleFeedbackSkipped = () =>
    dispatch(ArticleFeedbackActions.negativeFeedbackSkipped(articleId));

  const handleFeedbackSubmitted = (formData: FeedbackFormShape) =>
    dispatch(
      ArticleFeedbackActions.negativeFeedbackSubmit(
        articleId,
        article.title,
        formData
      )
    );

  const isLoading = useSelector((state) => state.articleFeedback.isLoading);
  const isFeedbackFormSubmitting = useSelector(
    (state) => state.articleFeedback.isFormSubmitting
  );
  const stage = useSelector((state) => state.articleFeedback.stage);

  const handleMessageChange = (
    eventOrValue: React.ChangeEvent<HTMLTextAreaElement> | string
  ) => {
    const val = getOnChangeValue(eventOrValue);
    setValues({ ...values, feedback: val });
  };

  const handleEmailChange = (
    eventOrValue: React.ChangeEvent<HTMLInputElement> | string
  ) => {
    const val = getOnChangeValue(eventOrValue);
    setValues({ ...values, email: val });
  };

  const messageError = !values.feedback ? t('required', 'Required') : null;

  const emailError =
    values.email && !values.email.match(/^.+@.+\..+$/)
      ? t('invalid_email', 'Invalid email')
      : null;

  const onSubmit = (event?: React.FormEvent<HTMLFormElement>) => {
    if (event) event.preventDefault();
    setHasSubmitted(true);

    // Bail and let the user fix the validation issues.
    if (messageError || emailError) return;

    // Show the anon submit message, if no email and field is shown.
    if (!showAnonymousWarning && !values.email && !emailFieldIsHidden) {
      setShowAnonymousWarning(true);
      return;
    }

    handleFeedbackSubmitted(values);
  };

  return {
    stage,
    isFeedbackEnabled: getFeedbackEnabled(),
    isLoading,
    isFeedbackFormSubmitting,
    values,
    emailFieldIsHidden,
    showAnonymousWarning,
    hasSubmitted,
    messageError: hasSubmitted ? messageError : null,
    emailError: hasSubmitted ? emailError : null,
    onPositiveReaction: handlePositive,
    onNegativeReaction: handleNegative,
    onFeedbackSkipped: handleFeedbackSkipped,
    handleEmailChange,
    handleMessageChange,
    onSubmit,
  };
}
